import { ComboInCart, SummaryData } from '../types/cart';
import { ComboDate } from '../types/ticket';

export type CartTotals = {
  /** What the tickets and combos cost, discounts applied, fees excluded. */
  subtotal: number;
  /** What the combo date discounts took off the combos' full price. Already reflected in `subtotal`. */
  comboDiscount: number;
  /** Ingresse service fee for the whole cart. */
  serviceFee: number;
  /** `subtotal + serviceFee`. Processing fee and interest are not known yet. */
  total: number;
};

/*
 * Summed in cents so a cart of R$ 0,10 lines does not drift to R$ 0,30000000000000004.
 *
 * Mirrors api-embedstore's `convert_to_cents` — `Decimal(str(value)) * 100`, ROUND_HALF_UP —
 * which is what turns the per-unit fee into the `serviceFee` the gateway charges. The shift
 * goes through the decimal string, not `value * 100`, because the float product lands just
 * below the half on values like 1.005 (100.49999…) and would round down where the gateway
 * rounds up. Inputs are never negative, so `Math.round` is half-up here.
 */
const shiftDecimal = (value: number, places: number): number => {
  const [mantissa, exponent = '0'] = String(value).split('e');
  return Number(`${mantissa}e${Number(exponent) + places}`);
};

/*
 * The payload contract types every amount as a number; a broken one must not turn the whole
 * summary into "R$ NaN", so a non-finite amount counts as zero.
 */
const toCents = (value: number): number => (Number.isFinite(value) ? Math.round(shiftDecimal(value, 2)) : 0);

/**
 * Rounds a sum of reais amounts to cents only once, after adding them up — what
 * `convert_to_cents(sum(...))` does over the SQL decimals. Each amount is taken to four decimal
 * places first, so the float addition in between cannot nudge the total across a half cent.
 */
const sumToCents = (values: number[]): number => {
  const tenThousandths = values.reduce(
    (acc, value) => acc + (Number.isFinite(value) ? Math.round(shiftDecimal(value, 4)) : 0),
    0,
  );
  return Math.round(tenThousandths / 100);
};

/** The ticket a combo date was bought with: the combo flow keeps only the selected one. */
const comboDateTicket = (date: ComboDate) => date.groups[0].tickets[0];

/*
 * One combo unit, priced the way api-embedstore's live path charges it: the `GROUP_COMBO` query
 * (raw_queries/sale_item.py) turns every unit into its own gateway item with
 * `sum(saleItem.price)` and `sum(saleItem.tax) as serviceFee`, `types_conversion` converts each
 * sum to cents, and the combo discount travels apart as `convert_to_cents(discount_in_currency)`
 * per unit. So the dates are added first and rounded once per unit — and the unit is rounded
 * before the quantity multiplies it. (`_update_combo_objects`, which rounds each date, is not on
 * that path: nothing calls it.)
 */
const comboUnitCents = (combo: ComboInCart) => {
  const tickets = combo.comboDates.map((date) => ({ ...comboDateTicket(date), discount : date.discount }));
  return {
    fullPrice : sumToCents(tickets.map(({ price }) => price)),
    discount : sumToCents(tickets.map(({ price, discount }) => (price * discount) / 100)),
    serviceFee : sumToCents(tickets.map(({ tax }) => tax)),
  };
};

/*
 * `TicketType.tax` is the per-unit service fee in reais (`guestType.tax` in the
 * legacy model). api-embedstore forwards that same catalog value to the gateway
 * as `serviceFee`, untouched by coupons — a coupon only lowers `price` — so
 * `toCents(tax) × quantity` is exactly what the buyer will be charged as service fee.
 */

export const computeCartTotals = ({
  summaryData,
  combosInCart,
}: {
  summaryData: SummaryData[];
  combosInCart: ComboInCart[];
}): CartTotals => {
  const ticketsCents = summaryData.reduce((acc, ticket) => acc + toCents(ticket.accumulatedValueInCart), 0);
  const ticketsFeeCents = summaryData
    .filter((ticket) => ticket.quantity > 0)
    .reduce((acc, ticket) => acc + toCents(ticket.tax) * ticket.quantity, 0);

  const combos = combosInCart.reduce(
    (acc, combo) => {
      const unit = comboUnitCents(combo);
      return {
        fullPrice : acc.fullPrice + unit.fullPrice * combo.quantity,
        discount : acc.discount + unit.discount * combo.quantity,
        serviceFee : acc.serviceFee + unit.serviceFee * combo.quantity,
      };
    },
    { fullPrice : 0, discount : 0, serviceFee : 0 },
  );

  const subtotalCents = ticketsCents + combos.fullPrice - combos.discount;
  const serviceFeeCents = ticketsFeeCents + combos.serviceFee;

  return {
    subtotal : subtotalCents / 100,
    comboDiscount : combos.discount / 100,
    serviceFee : serviceFeeCents / 100,
    total : (subtotalCents + serviceFeeCents) / 100,
  };
};
